import { onLinkMessage, type MessagePortLike } from "./protocol.js";
import type { ModelCommand, ModelStatus } from "@cuekards/core/model-download/status";

/**
 * The line between the page and the recognition worker about the model download
 * (web ticket 15).
 *
 * A third channel, beside the renderer-to-main contract and `speech-link.ts`, and
 * it is a third one for a reason rather than by accretion.
 *
 * **Not the contract.** The contract is the desktop app's renderer-to-main
 * contract, unchanged, and that is what lets `@cuekards/ui`'s components compile
 * against both hosts. The desktop has no model download — the model is a file
 * beside the binary — so a channel for one would be a channel only ever
 * implemented on one side, which is the thing web ticket 14 declined to do for
 * alignments and this declines to do for the same reason.
 *
 * **Not the speech link.** That link is between the two *workers*: what the core
 * has to tell the follower, and what the follower has to tell the core. Nothing
 * about a download is either. The core worker has no opinion about how many bytes
 * have arrived, and routing the status through it would put two hops and a worker
 * that is writing a take's bytes to storage between a progress bar and the thing
 * making progress.
 *
 * So this is the page talking to the recognition worker directly, which is the
 * same short way the microphone fork goes and for a milder version of the same
 * reason: the page is where the button is, and the worker is where the fetch is.
 */

/**
 * What the worker is doing about the model, and what the page asks of it.
 *
 * Both unions are `@cuekards/core`'s and are re-exported from here rather than
 * declared. They moved with the loader (mobile ticket 02), because they are what
 * the machine reports and accepts rather than anything about a `MessagePort`: a
 * phone runs the same loader in process and draws the same seven phases with no
 * link at all. They are re-exported because every reader of them on this host —
 * the download panel, the prompter's chrome, the follower's failure sentence —
 * meets them as what arrives on this link, and repointing each at core would say
 * only that a type has moved house.
 */
export type { ModelCommand, ModelStatus };

export const MODEL_STATUS = "model-status";
export const MODEL_COMMAND = "model-command";

export interface ModelStatusMessage {
  kind: "model-status";
  status: ModelStatus;
}

export interface ModelCommandMessage {
  kind: "model-command";
  command: ModelCommand;
}

/** The status the page assumes before the worker has said anything — including
 * forever, on a deployment where the worker's script does not exist. */
export const UNKNOWN_MODEL: ModelStatus = { phase: "checking" };

export function postModelStatus(port: MessagePortLike, status: ModelStatus): void {
  port.postMessage({ kind: MODEL_STATUS, status } satisfies ModelStatusMessage);
}

export function sendModelCommand(
  port: MessagePortLike,
  command: ModelCommand,
): void {
  port.postMessage({ kind: MODEL_COMMAND, command } satisfies ModelCommandMessage);
}

/**
 * Listen for status, or for commands, on a port shared with other traffic.
 *
 * Both directions of this link run over the recognition worker's own global
 * scope, which already carries the speech-link handover and eight audio frames a
 * second — which is exactly the situation `onLinkMessage` exists for, so it is
 * used rather than reproduced here with one kind instead of six.
 */
export function onModelStatus(
  port: MessagePortLike,
  handle: (status: ModelStatus) => void,
): () => void {
  return onLinkMessage<ModelStatusMessage>(port, [MODEL_STATUS], (message) =>
    handle(message.status),
  );
}

export function onModelCommand(
  port: MessagePortLike,
  handle: (command: ModelCommand) => void,
): () => void {
  return onLinkMessage<ModelCommandMessage>(port, [MODEL_COMMAND], (message) =>
    handle(message.command),
  );
}
